import { chSupabase } from '../lib/supabase';
import { chNotificationService } from './notificationService';
import { getAssigneeIds, getMediaUrls } from '../lib/posts';
import type { ChPostMetrics } from '../lib/metrics';
import type {
  ChPost,
  ChPostStatus,
  ChComment,
  ChCommentType,
  ChApprovalAction,
  ChPlatformMetrics,
} from '../types';
import { POST_STATUS_LABELS as POST_STATUS_NAMES } from '../types';

export interface ChPostVersion {
  id: string;
  post_id: string;
  version_number: number;
  title: string;
  caption: string | null;
  media_url: string | null;
  /** Absent until the v2 SQL is applied. */
  media_urls?: string[] | null;
  thumbnail_url?: string | null;
  note?: string | null;
  author_id?: string | null;
  author_name?: string | null;
  created_at: string;
}

export interface ChApproval {
  id: string;
  post_id: string;
  user_id: string;
  user_name: string;
  action: ChApprovalAction;
  note: string | null;
  created_at: string;
}

export interface ChVersionInfo {
  note?: string | null;
  authorId?: string | null;
  authorName?: string | null;
}

const V2_SQL_MISSING = 'Falta aplicar o SQL do Content Hub v2 no Supabase (20261028120000_content_hub_v2.sql)';

function isMissingColumn(error: { code?: string } | null): boolean {
  return error?.code === 'PGRST204';
}

/** The single columns mirror the first item of each list (older code reads them). */
function withLegacyColumns<T extends Partial<ChPost>>(post: T): T {
  const out = { ...post };
  if (post.platforms && post.platforms.length > 0) out.platform = post.platforms[0];
  if (post.assignee_ids) out.assignee_id = post.assignee_ids[0] ?? null;
  if (post.media_urls) out.media_url = post.media_urls[0] ?? null;
  return out;
}

/** Files and text are what a version is about; dates and people are not. */
function contentChanged(before: ChPost, after: Partial<ChPost>): boolean {
  if (after.title !== undefined && after.title !== before.title) return true;
  if (after.caption !== undefined && (after.caption || null) !== (before.caption || null)) return true;
  if (after.thumbnail_url !== undefined && (after.thumbnail_url || null) !== (before.thumbnail_url || null)) return true;
  if (after.media_urls !== undefined || after.media_url !== undefined) {
    const next = after.media_urls ?? (after.media_url ? [after.media_url] : []);
    if (JSON.stringify(next) !== JSON.stringify(getMediaUrls(before))) return true;
  }
  return false;
}

async function insertVersion(post: ChPost, versionNumber: number, info: ChVersionInfo = {}) {
  const base = {
    post_id: post.id,
    version_number: versionNumber,
    title: post.title,
    caption: post.caption,
    media_url: post.media_url,
  };
  const { error } = await chSupabase.from('ch_post_versions').insert({
    ...base,
    media_urls: getMediaUrls(post),
    thumbnail_url: post.thumbnail_url,
    note: info.note?.trim() || null,
    author_id: info.authorId ?? null,
    author_name: info.authorName ?? null,
  });
  // Before the v2 SQL the history only has text and the first file
  if (isMissingColumn(error)) await chSupabase.from('ch_post_versions').insert(base);
}

export const chPostService = {
  async getByProject(projectId: string): Promise<ChPost[]> {
    const { data, error } = await chSupabase
      .from('ch_posts')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ChPost[];
  },

  async getById(id: string): Promise<ChPost | null> {
    const { data, error } = await chSupabase
      .from('ch_posts')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as ChPost | null;
  },

  async create(
    post: Omit<ChPost, 'id' | 'created_at' | 'updated_at' | 'current_version'>,
    info: ChVersionInfo = {}
  ): Promise<ChPost> {
    const { data, error } = await chSupabase
      .from('ch_posts')
      .insert({ ...withLegacyColumns(post), current_version: 1 })
      .select()
      .single();

    if (isMissingColumn(error)) throw new Error(V2_SQL_MISSING);
    if (error) throw error;
    const created = data as ChPost;
    await insertVersion(created, 1, info);
    return created;
  },

  /**
   * Saves changes. A new version is recorded only when the content changed
   * (title, copy, files, cover) — the previous files stay in the history.
   */
  async update(id: string, updates: Partial<ChPost>, info: ChVersionInfo = {}): Promise<ChPost> {
    const before = await this.getById(id);
    if (!before) throw new Error('Post não encontrado');
    const isNewVersion = contentChanged(before, updates);

    const { data, error } = await chSupabase
      .from('ch_posts')
      .update({
        ...withLegacyColumns(updates),
        ...(isNewVersion ? { current_version: before.current_version + 1 } : {}),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (isMissingColumn(error)) throw new Error(V2_SQL_MISSING);
    if (error) throw error;
    const updated = data as ChPost;
    if (isNewVersion) await insertVersion(updated, updated.current_version, info);
    return updated;
  },

  async delete(id: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_posts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /** Saves stats (and when it went live) without creating a new content version. */
  async updateMetrics(
    id: string,
    metrics: ChPostMetrics,
    extra: { metrics_by_platform?: ChPlatformMetrics | null; published_at?: string | null } = {}
  ): Promise<ChPost> {
    const { data, error } = await chSupabase
      .from('ch_posts')
      .update({ metrics, ...extra, metrics_updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (isMissingColumn(error)) {
      throw new Error(Object.keys(extra).length > 0
        ? V2_SQL_MISSING
        : 'Falta aplicar o SQL das stats no Supabase (20260928140000_content_hub_stats_and_prints.sql)');
    }
    if (error) throw error;
    return data as ChPost;
  },

  async changeStatus(
    postId: string,
    newStatus: ChPostStatus,
    userId: string,
    userName: string,
    note?: string
  ): Promise<void> {
    const post = await this.getById(postId);
    if (!post) throw new Error('Post não encontrado');

    const { error } = await chSupabase
      .from('ch_posts')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', postId);

    if (error) throw error;

    let action: ChApprovalAction = 'internal_approved';
    if (newStatus === 'aprovado') action = 'client_approved';
    else if (newStatus === 'publicado') action = 'published';
    else if (newStatus === 'em_producao' || newStatus === 'em_revisao') action = 'changes_requested';
    else if (newStatus === 'pronto') action = 'internal_approved';

    await chSupabase.from('ch_approvals').insert({
      post_id: postId,
      user_id: userId,
      user_name: userName,
      action,
      note: note || null,
    });

    if (newStatus === 'aprovado') {
      await chNotificationService.notifyProjectMembers(
        post.project_id,
        userId,
        postId,
        'approved',
        `"${post.title}" foi aprovado internamente por ${userName}`
      );
    }

    await Promise.all(getAssigneeIds(post)
      .filter(id => id !== userId)
      .map(id => chNotificationService.create({
        user_id: id,
        post_id: postId,
        project_id: post.project_id,
        type: 'assigned',
        message: `"${post.title}" mudou para ${POST_STATUS_NAMES[newStatus]} por ${userName}`,
      }).catch(() => { /* the status is saved */ })));
  },

  async getVersions(postId: string): Promise<ChPostVersion[]> {
    const { data, error } = await chSupabase
      .from('ch_post_versions')
      .select('*')
      .eq('post_id', postId)
      .order('version_number', { ascending: false });

    if (error) throw error;
    return (data || []) as ChPostVersion[];
  },

  async getApprovals(postId: string): Promise<ChApproval[]> {
    const { data, error } = await chSupabase
      .from('ch_approvals')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ChApproval[];
  },

  async uploadMedia(file: File, projectId: string): Promise<string> {
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
    // Several files can be uploaded in the same millisecond (carousels)
    const path = `${projectId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

    const { error } = await chSupabase.storage
      .from('ch-media')
      .upload(path, file, { upsert: false });

    if (error) throw error;

    const { data } = chSupabase.storage.from('ch-media').getPublicUrl(path);
    return data.publicUrl;
  },
};

export const chCommentService = {
  async getByPost(postId: string): Promise<ChComment[]> {
    const { data, error } = await chSupabase
      .from('ch_comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []) as ChComment[];
  },

  async create(comment: {
    post_id: string;
    author_id: string;
    author_name: string;
    type: ChCommentType;
    text: string;
    mentions?: string[];
  }): Promise<ChComment> {
    const payload = comment.mentions && comment.mentions.length > 0
      ? comment
      : (({ mentions: _omit, ...rest }) => rest)(comment);
    const { data, error } = await chSupabase
      .from('ch_comments')
      .insert(payload)
      .select()
      .single();

    // PGRST204: mentions column not created yet — keep the comment, drop tags
    if (error?.code === 'PGRST204' && 'mentions' in payload) {
      const { mentions: _omit, ...legacy } = payload;
      const retry = await chSupabase.from('ch_comments').insert(legacy).select().single();
      if (retry.error) throw retry.error;
      return retry.data as ChComment;
    }
    if (error) throw error;
    return data as ChComment;
  },

  async delete(id: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_comments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
